import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Taskbar from './Taskbar';
import LinkedInWindow from './LinkedInWindow';
import GitHubWindow from './GitHubWindow';
import MailWindow from './MailWindow';
import DigitalClock from './DigitalClock';
import FakeTerminal from './FakeTerminal';
import AuthGate from './AuthGate';
import { useGatedContent } from '../hooks/useGatedContent';
import { pinnedApps } from '../data/apps';

interface RecentApp {
  id: string;
  name: string;
  icon: string;
  isCustomIcon?: boolean;
  lastUsed: Date;
}

/**
 * Renders the body of the File Explorer window using gated Firestore content.
 */
const FileExplorerContent: React.FC = () => {
  const { data, loading, error } = useGatedContent('file-explorer');

  if (loading) return <div style={{ padding: '2rem', color: '#666' }}>Loading...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#d13438' }}>{error}</div>;
  if (!data) return <div style={{ padding: '2rem', color: '#666' }}>No content available.</div>;

  return (
    <div style={{ padding: '2rem' }}>
      <h2>{data.title}</h2>
      <p>{data.content}</p>
    </div>
  );
};

interface FileExplorerWindowProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  zIndex: number;
  onClick: () => void;
}

/**
 * File Explorer window with an AuthGate wrapper around its content.
 * @param {FileExplorerWindowProps} props - Standard window props (open, maximize, minimize, close, zIndex, onClick).
 */
const FileExplorerWindow: React.FC<FileExplorerWindowProps> = ({
  isOpen, isMaximized, onClose, onMaximize, onMinimize, zIndex, onClick
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`window-overlay file-explorer ${isMaximized ? 'maximized' : ''}`}
      style={{ zIndex }}
      onClick={onClick}
    >
      <div className={`window ${isMaximized ? 'maximized' : ''}`}>
        <div className="window-titlebar">
          <div className="window-title">
            <span className="window-icon">📁</span>
            File Explorer
          </div>
          <div className="window-controls">
            <button className="window-control minimize" onClick={(e) => { e.stopPropagation(); onMinimize(); }}>−</button>
            <button className="window-control maximize" onClick={(e) => { e.stopPropagation(); onMaximize(); }}>{isMaximized ? '🗗' : '⬜'}</button>
            <button className="window-control close" onClick={(e) => { e.stopPropagation(); onClose(); }}>✕</button>
          </div>
        </div>
        <div className="window-content">
          <AuthGate>
            <FileExplorerContent />
          </AuthGate>
        </div>
      </div>
    </div>
  );
};

/**
 * Root desktop component that manages window state, z-ordering, and recent-app tracking.
 * Renders the taskbar, clock, and all application windows.
 */
const Desktop: React.FC = () => {
  const navigate = useNavigate();
  const [openWindows, setOpenWindows] = useState<{ [key: string]: boolean }>({
    linkedin: false,
    github: false,
    mail: false,
    terminal: true,
    'file-explorer': false,
  });

  const [maximizedWindows, setMaximizedWindows] = useState<{ [key: string]: boolean }>({
    linkedin: false,
    github: false,
    mail: false,
    terminal: false,
    'file-explorer': false,
  });

  const [recentApps, setRecentApps] = useState<RecentApp[]>([]);
  const [windowOrder, setWindowOrder] = useState<string[]>(['terminal', 'linkedin', 'github', 'mail', 'file-explorer']);

  const bringToFront = (windowId: string) => {
    setWindowOrder(prev => {
      const filtered = prev.filter(id => id !== windowId);
      return [...filtered, windowId];
    });
  };

  const handleAppClick = (appId: string) => {
    const app = pinnedApps.find(a => a.id === appId);
    if (app?.route) {
      navigate(app.route);
      return;
    }

    setOpenWindows(prev => {
      const isCurrentlyOpen = prev[appId];

      if (isCurrentlyOpen) {
        return {
          ...prev,
          [appId]: false
        };
      } else {
        bringToFront(appId);
        return {
          ...prev,
          [appId]: true
        };
      }
    });

    if (app) {
      setRecentApps(prev => {
        const filtered = prev.filter(recentApp => recentApp.id !== appId);
        const newRecentApp: RecentApp = {
          id: app.id,
          name: app.name,
          icon: app.icon,
          isCustomIcon: app.isCustomIcon,
          lastUsed: new Date()
        };
        return [newRecentApp, ...filtered].slice(0, 5);
      });
    }
  };

  const handleWindowClose = (appId: string) => {
    setOpenWindows(prev => ({
      ...prev,
      [appId]: false
    }));
    setMaximizedWindows(prev => ({
      ...prev,
      [appId]: false
    }));
  };

  const handleWindowMaximize = (appId: string) => {
    setMaximizedWindows(prev => ({
      ...prev,
      [appId]: !prev[appId]
    }));
  };

  const handleWindowMinimize = (appId: string) => {
    handleWindowClose(appId);
  };

  return (
    <div className="desktop">
      <div className="desktop-background"></div>
      <DigitalClock />
      <Taskbar onAppClick={handleAppClick} recentApps={recentApps} />

      <LinkedInWindow
        isOpen={openWindows.linkedin}
        isMaximized={maximizedWindows.linkedin}
        onClose={() => handleWindowClose('linkedin')}
        onMaximize={() => handleWindowMaximize('linkedin')}
        onMinimize={() => handleWindowMinimize('linkedin')}
        zIndex={1000 + windowOrder.indexOf('linkedin')}
        onClick={() => bringToFront('linkedin')}
      />

      <GitHubWindow
        isOpen={openWindows.github}
        isMaximized={maximizedWindows.github}
        onClose={() => handleWindowClose('github')}
        onMaximize={() => handleWindowMaximize('github')}
        onMinimize={() => handleWindowMinimize('github')}
        zIndex={1000 + windowOrder.indexOf('github')}
        onClick={() => bringToFront('github')}
      />

      <MailWindow
        isOpen={openWindows.mail}
        isMaximized={maximizedWindows.mail}
        onClose={() => handleWindowClose('mail')}
        onMaximize={() => handleWindowMaximize('mail')}
        onMinimize={() => handleWindowMinimize('mail')}
        zIndex={1000 + windowOrder.indexOf('mail')}
        onClick={() => bringToFront('mail')}
      />

      <FakeTerminal
        isOpen={openWindows.terminal}
        isMaximized={maximizedWindows.terminal}
        onClose={() => handleWindowClose('terminal')}
        onMaximize={() => handleWindowMaximize('terminal')}
        onMinimize={() => handleWindowMinimize('terminal')}
        zIndex={1000 + windowOrder.indexOf('terminal')}
        onClick={() => bringToFront('terminal')}
      />

      <FileExplorerWindow
        isOpen={openWindows['file-explorer']}
        isMaximized={maximizedWindows['file-explorer']}
        onClose={() => handleWindowClose('file-explorer')}
        onMaximize={() => handleWindowMaximize('file-explorer')}
        onMinimize={() => handleWindowMinimize('file-explorer')}
        zIndex={1000 + windowOrder.indexOf('file-explorer')}
        onClick={() => bringToFront('file-explorer')}
      />
    </div>
  );
};

export default Desktop;
